import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { AppNav } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Save, Zap, ChevronDown, ChevronUp, X, CheckCircle, Terminal } from "lucide-react";
import { toast } from "sonner";
import CurlImportModal from "@/components/CurlImportModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const METHOD_COLORS = {
  GET: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  POST: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  PUT: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  PATCH: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  DELETE: "bg-red-500/20 text-red-400 border-red-500/30",
};

const ASSERTION_TYPES = [
  { value: "status_code", label: "Status Code equals" },
  { value: "body_contains", label: "Body contains" },
  { value: "body_equals", label: "Body equals" },
  { value: "json_path", label: "JSON path equals" },
  { value: "header_contains", label: "Header contains" },
  { value: "response_time", label: "Response time ≤ (ms)" },
];

function newStep(number) {
  return {
    step_number: number,
    name: "",
    method: "GET",
    url: "",
    headers: [],      // [{key, value}]
    body: "",
    assertions: [],   // [{id, type, target, expected}]
    extract: [],      // [{var, path}]
    expanded: true,
  };
}

function newAssertion() {
  return { id: Date.now(), type: "status_code", target: "", expected: "200" };
}

// Convert a parsed step (from curl or collection) into the UI step format
function parsedToUIStep(parsed, number) {
  return {
    step_number: number,
    name: parsed.name || "",
    method: parsed.method || "GET",
    url: parsed.url || "",
    headers: Object.entries(parsed.headers || {}).map(([key, value]) => ({ key, value })),
    body: parsed.body || "",
    assertions: (parsed.assertions || []).map(a => ({ id: Date.now() + Math.random(), ...a })),
    extract: Object.entries(parsed.extract || {}).map(([varName, path]) => ({ var: varName, path })),
    expanded: true,
  };
}

export default function CreateAPITest() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefill = location.state || {};

  const [saving, setSaving] = useState(false);

  // cURL import modal — tracks which step index to fill (-1 = new step)
  const [curlModalTarget, setCurlModalTarget] = useState(null); // null | number

  // Top-level fields — pre-fill from collection import if provided
  const [name, setName] = useState(prefill.testName || "");
  const [description, setDescription] = useState(prefill.testDescription || "");
  const [baseUrl, setBaseUrl] = useState(prefill.baseUrl || prefill.apiConfig?.base_url || "");
  const [variables, setVariables] = useState(() => {
    const vars = prefill.apiConfig?.variables || {};
    const entries = Object.entries(vars).filter(([k]) => k !== "base_url");
    return entries.length > 0
      ? entries.map(([key, value]) => ({ key, value }))
      : [{ key: "", value: "" }];
  });
  const [defaultHeaders, setDefaultHeaders] = useState(() => {
    const hdrs = prefill.apiConfig?.default_headers || {};
    const entries = Object.entries(hdrs);
    return entries.length > 0
      ? entries.map(([key, value]) => ({ key, value }))
      : [{ key: "Content-Type", value: "application/json" }];
  });

  // Steps — pre-fill from cURL or collection import
  const [steps, setSteps] = useState(() => {
    if (prefill.prefillSteps && prefill.prefillSteps.length > 0) {
      return prefill.prefillSteps.map((ps, i) => parsedToUIStep(ps, i + 1));
    }
    return [newStep(1)];
  });

  // Handle curl import for a specific step
  const handleCurlImport = (parsedStep) => {
    const target = curlModalTarget;
    if (target === null) return;

    if (target === -1) {
      // Append as a new step
      const uiStep = parsedToUIStep(parsedStep, steps.length + 1);
      setSteps(prev => [...prev, uiStep]);
    } else {
      // Replace step at target index
      setSteps(prev => prev.map((s, i) =>
        i === target
          ? { ...parsedToUIStep(parsedStep, s.step_number), expanded: true }
          : s
      ));
    }
    setCurlModalTarget(null);
  };

  // ── Helpers ────────────────────────────────────────────────────────────
  const updateStep = (idx, field, value) =>
    setSteps(prev => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));

  const addStep = () =>
    setSteps(prev => [...prev, newStep(prev.length + 1)]);

  const removeStep = (idx) =>
    setSteps(prev =>
      prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_number: i + 1 }))
    );

  const toggleExpand = (idx) =>
    updateStep(idx, "expanded", !steps[idx].expanded);

  // Headers within a step
  const addStepHeader = (idx) =>
    updateStep(idx, "headers", [...steps[idx].headers, { key: "", value: "" }]);

  const updateStepHeader = (stepIdx, hIdx, field, val) =>
    updateStep(stepIdx, "headers", steps[stepIdx].headers.map((h, i) =>
      i === hIdx ? { ...h, [field]: val } : h
    ));

  const removeStepHeader = (stepIdx, hIdx) =>
    updateStep(stepIdx, "headers", steps[stepIdx].headers.filter((_, i) => i !== hIdx));

  // Assertions
  const addAssertion = (idx) =>
    updateStep(idx, "assertions", [...steps[idx].assertions, newAssertion()]);

  const updateAssertion = (stepIdx, aIdx, field, val) =>
    updateStep(stepIdx, "assertions", steps[stepIdx].assertions.map((a, i) =>
      i === aIdx ? { ...a, [field]: val } : a
    ));

  const removeAssertion = (stepIdx, aIdx) =>
    updateStep(stepIdx, "assertions", steps[stepIdx].assertions.filter((_, i) => i !== aIdx));

  // Extract variables
  const addExtract = (idx) =>
    updateStep(idx, "extract", [...steps[idx].extract, { var: "", path: "" }]);

  const updateExtract = (stepIdx, eIdx, field, val) =>
    updateStep(stepIdx, "extract", steps[stepIdx].extract.map((e, i) =>
      i === eIdx ? { ...e, [field]: val } : e
    ));

  const removeExtract = (stepIdx, eIdx) =>
    updateStep(stepIdx, "extract", steps[stepIdx].extract.filter((_, i) => i !== eIdx));

  // Global variables
  const updateVariable = (idx, field, val) =>
    setVariables(prev => prev.map((v, i) => (i === idx ? { ...v, [field]: val } : v)));

  const updateDefaultHeader = (idx, field, val) =>
    setDefaultHeaders(prev => prev.map((h, i) => (i === idx ? { ...h, [field]: val } : h)));

  // ── Save ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!name.trim()) { toast.error("Test name is required"); return; }
    if (steps.some(s => !s.url.trim())) { toast.error("All steps need a URL"); return; }

    setSaving(true);
    try {
      // Build api_config
      const varsObj = {};
      variables.filter(v => v.key.trim()).forEach(v => { varsObj[v.key.trim()] = v.value; });
      if (baseUrl.trim()) varsObj["base_url"] = baseUrl.trim();

      const defHdrs = {};
      defaultHeaders.filter(h => h.key.trim()).forEach(h => { defHdrs[h.key.trim()] = h.value; });

      // Build api_steps
      const apiSteps = steps.map(s => {
        const headers = {};
        s.headers.filter(h => h.key.trim()).forEach(h => { headers[h.key.trim()] = h.value; });

        const extract = {};
        s.extract.filter(e => e.var.trim() && e.path.trim()).forEach(e => { extract[e.var.trim()] = e.path.trim(); });

        return {
          step_number: s.step_number,
          name: s.name || `Step ${s.step_number}`,
          method: s.method,
          url: s.url,
          headers,
          body: s.body || null,
          assertions: s.assertions.map(a => ({
            type: a.type,
            target: a.target || null,
            expected: a.expected,
          })),
          extract,
        };
      });

      await axios.post(`${API}/test-cases`, {
        name,
        description,
        test_type: "api",
        api_config: { base_url: baseUrl, variables: varsObj, default_headers: defHdrs },
        api_steps: apiSteps,
        steps: [],  // no UI steps for API tests
        tags: [],
        is_shared_with_team: false,
      }, { withCredentials: true });

      toast.success("API test created!");
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      toast.error("Failed to save API test");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black text-white">
      <AppNav />
      <div className="max-w-4xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
              <Zap className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">New API Test</h1>
              <p className="text-slate-400 text-sm">Test REST endpoints with assertions and chained requests</p>
            </div>
          </div>

          {/* Basic Info */}
          <Card className="glass-card border-white/10 mb-6">
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label className="text-slate-300 mb-1 block">Test Name *</Label>
                <Input
                  placeholder="e.g. Login API Flow"
                  className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-slate-300 mb-1 block">Description</Label>
                <Textarea
                  placeholder="What does this API test cover?"
                  className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm resize-none"
                  rows={2}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Base Configuration */}
          <Card className="glass-card border-white/10 mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-slate-200">Base Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Base URL */}
              <div>
                <Label className="text-slate-400 text-xs mb-1 block">Base URL (available as {"{{base_url}}"})</Label>
                <Input
                  placeholder="https://api.myapp.com"
                  className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm font-mono text-sm"
                  value={baseUrl}
                  onChange={e => setBaseUrl(e.target.value)}
                />
              </div>

              {/* Variables */}
              <div>
                <Label className="text-slate-400 text-xs mb-2 block">Variables (use as {"{{variable_name}}"})</Label>
                <div className="space-y-2">
                  {variables.map((v, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        placeholder="variable_name"
                        className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm font-mono flex-1"
                        value={v.key}
                        onChange={e => updateVariable(idx, "key", e.target.value)}
                      />
                      <span className="text-slate-500 self-center">=</span>
                      <Input
                        placeholder="default value"
                        className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm flex-1"
                        value={v.value}
                        onChange={e => updateVariable(idx, "value", e.target.value)}
                      />
                      <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 flex-shrink-0"
                        onClick={() => setVariables(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 text-xs"
                    onClick={() => setVariables(prev => [...prev, { key: "", value: "" }])}>
                    <Plus className="w-3 h-3 mr-1" /> Add Variable
                  </Button>
                </div>
              </div>

              {/* Default Headers */}
              <div>
                <Label className="text-slate-400 text-xs mb-2 block">Default Headers (applied to every step)</Label>
                <div className="space-y-2">
                  {defaultHeaders.map((h, idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        placeholder="Header-Name"
                        className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm font-mono flex-1"
                        value={h.key}
                        onChange={e => updateDefaultHeader(idx, "key", e.target.value)}
                      />
                      <span className="text-slate-500 self-center">:</span>
                      <Input
                        placeholder="value"
                        className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm flex-1"
                        value={h.value}
                        onChange={e => updateDefaultHeader(idx, "value", e.target.value)}
                      />
                      <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 flex-shrink-0"
                        onClick={() => setDefaultHeaders(prev => prev.filter((_, i) => i !== idx))}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 text-xs"
                    onClick={() => setDefaultHeaders(prev => [...prev, { key: "", value: "" }])}>
                    <Plus className="w-3 h-3 mr-1" /> Add Header
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Steps */}
          <div className="space-y-4 mb-6">
            <h2 className="text-lg font-semibold text-slate-200">Request Steps</h2>
            {steps.map((step, idx) => (
              <Card key={step.step_number} className="glass-card border-white/10">
                {/* Step Header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-white/5 cursor-pointer"
                  onClick={() => toggleExpand(idx)}>
                  <div className="w-7 h-7 bg-blue-500/20 rounded-sm flex items-center justify-center flex-shrink-0">
                    <span className="text-blue-400 font-bold text-sm">{step.step_number}</span>
                  </div>
                  <Badge className={`text-xs font-mono border ${METHOD_COLORS[step.method] || METHOD_COLORS.GET}`}>
                    {step.method}
                  </Badge>
                  <span className="text-slate-300 text-sm flex-1 truncate font-mono">
                    {step.url || <span className="text-slate-600">No URL yet</span>}
                  </span>
                  <div className="flex items-center gap-2">
                    {step.assertions.length > 0 && (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />{step.assertions.length} assertions
                      </Badge>
                    )}
                    <Button type="button" size="icon" variant="ghost"
                      className="text-slate-500 hover:text-blue-400 w-7 h-7"
                      title="Fill from cURL"
                      onClick={e => { e.stopPropagation(); setCurlModalTarget(idx); }}>
                      <Terminal className="w-3 h-3" />
                    </Button>
                    {steps.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-7 h-7"
                        onClick={e => { e.stopPropagation(); removeStep(idx); }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                    {step.expanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </div>

                {/* Step Body */}
                {step.expanded && (
                  <CardContent className="pt-4 space-y-4">
                    {/* Name + Method + URL */}
                    <div className="grid grid-cols-12 gap-3">
                      <div className="col-span-12">
                        <Label className="text-slate-400 text-xs mb-1 block">Step Name</Label>
                        <Input
                          placeholder="e.g. Login and get token"
                          className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm"
                          value={step.name}
                          onChange={e => updateStep(idx, "name", e.target.value)}
                        />
                      </div>
                      <div className="col-span-3">
                        <Label className="text-slate-400 text-xs mb-1 block">Method</Label>
                        <Select value={step.method} onValueChange={v => updateStep(idx, "method", v)}>
                          <SelectTrigger className="bg-black/40 border-white/10 text-white rounded-sm">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-slate-900 border-white/10">
                            {HTTP_METHODS.map(m => (
                              <SelectItem key={m} value={m} className="text-white hover:bg-white/5">{m}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-9">
                        <Label className="text-slate-400 text-xs mb-1 block">URL</Label>
                        <Input
                          placeholder="{{base_url}}/api/login"
                          className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-sm font-mono"
                          value={step.url}
                          onChange={e => updateStep(idx, "url", e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Headers */}
                    <div>
                      <Label className="text-slate-400 text-xs mb-2 block">Request Headers (override defaults)</Label>
                      <div className="space-y-2">
                        {step.headers.map((h, hIdx) => (
                          <div key={hIdx} className="flex gap-2">
                            <Input placeholder="Header-Name" className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono flex-1"
                              value={h.key} onChange={e => updateStepHeader(idx, hIdx, "key", e.target.value)} />
                            <span className="text-slate-500 self-center text-xs">:</span>
                            <Input placeholder="value" className="bg-black/40 border-white/10 text-white rounded-sm text-xs flex-1"
                              value={h.value} onChange={e => updateStepHeader(idx, hIdx, "value", e.target.value)} />
                            <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-7 h-7"
                              onClick={() => removeStepHeader(idx, hIdx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="text-slate-400 hover:text-white text-xs"
                          onClick={() => addStepHeader(idx)}>
                          <Plus className="w-3 h-3 mr-1" /> Add Header
                        </Button>
                      </div>
                    </div>

                    {/* Body */}
                    {["POST", "PUT", "PATCH"].includes(step.method) && (
                      <div>
                        <Label className="text-slate-400 text-xs mb-1 block">Request Body (JSON)</Label>
                        <Textarea
                          placeholder={'{\n  "email": "user@example.com",\n  "password": "{{password}}"\n}'}
                          className="bg-black/40 border-white/10 focus:border-blue-400 text-white rounded-sm text-xs font-mono resize-none"
                          rows={5}
                          value={step.body}
                          onChange={e => updateStep(idx, "body", e.target.value)}
                        />
                      </div>
                    )}

                    {/* Assertions */}
                    <div>
                      <Label className="text-slate-400 text-xs mb-2 block">Assertions</Label>
                      <div className="space-y-2">
                        {step.assertions.map((a, aIdx) => (
                          <div key={a.id} className="flex gap-2 items-center bg-black/20 rounded-sm p-2 border border-white/5">
                            <Select value={a.type} onValueChange={v => updateAssertion(idx, aIdx, "type", v)}>
                              <SelectTrigger className="bg-black/40 border-white/10 text-white rounded-sm text-xs w-48 flex-shrink-0">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-900 border-white/10">
                                {ASSERTION_TYPES.map(t => (
                                  <SelectItem key={t.value} value={t.value} className="text-white hover:bg-white/5 text-xs">{t.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {(a.type === "json_path" || a.type === "header_contains") && (
                              <Input
                                placeholder={a.type === "json_path" ? "data.user.id" : "Authorization"}
                                className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono w-36 flex-shrink-0"
                                value={a.target}
                                onChange={e => updateAssertion(idx, aIdx, "target", e.target.value)}
                              />
                            )}
                            <span className="text-slate-500 text-xs flex-shrink-0">→</span>
                            <Input
                              placeholder="expected value"
                              className="bg-black/40 border-white/10 text-white rounded-sm text-xs flex-1"
                              value={a.expected}
                              onChange={e => updateAssertion(idx, aIdx, "expected", e.target.value)}
                            />
                            <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-6 h-6 flex-shrink-0"
                              onClick={() => removeAssertion(idx, aIdx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300 text-xs"
                          onClick={() => addAssertion(idx)}>
                          <Plus className="w-3 h-3 mr-1" /> Add Assertion
                        </Button>
                      </div>
                    </div>

                    {/* Extract variables */}
                    <div>
                      <Label className="text-slate-400 text-xs mb-2 block">
                        Extract from Response (use in later steps as {"{{var}}"})
                      </Label>
                      <div className="space-y-2">
                        {step.extract.map((e, eIdx) => (
                          <div key={eIdx} className="flex gap-2 items-center">
                            <Input placeholder="variable_name" className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono flex-1"
                              value={e.var} onChange={ev => updateExtract(idx, eIdx, "var", ev.target.value)} />
                            <span className="text-slate-500 text-xs">←</span>
                            <Input placeholder="json.path.here" className="bg-black/40 border-white/10 text-white rounded-sm text-xs font-mono flex-1"
                              value={e.path} onChange={ev => updateExtract(idx, eIdx, "path", ev.target.value)} />
                            <Button type="button" size="icon" variant="ghost" className="text-slate-500 hover:text-red-400 w-6 h-6"
                              onClick={() => removeExtract(idx, eIdx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                        <Button type="button" variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300 text-xs"
                          onClick={() => addExtract(idx)}>
                          <Plus className="w-3 h-3 mr-1" /> Extract Variable
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}

            {/* Add Step buttons */}
            <div className="grid grid-cols-2 gap-3">
              <Button type="button" variant="outline"
                className="border-dashed border-white/20 text-slate-400 hover:text-white hover:border-blue-400 hover:bg-blue-400/5 rounded-sm"
                onClick={addStep}>
                <Plus className="w-4 h-4 mr-2" /> Add Request Step
              </Button>
              <Button type="button" variant="outline"
                className="border-dashed border-blue-500/30 text-blue-400 hover:text-white hover:border-blue-400 hover:bg-blue-400/5 rounded-sm"
                onClick={() => setCurlModalTarget(-1)}>
                <Terminal className="w-4 h-4 mr-2" /> Add Step from cURL
              </Button>
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" className="text-slate-400" onClick={() => navigate("/dashboard")}>
              Cancel
            </Button>
            <Button
              className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm font-semibold"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <span className="flex items-center gap-2"><span className="animate-spin">⟳</span> Saving...</span>
              ) : (
                <span className="flex items-center gap-2"><Save className="w-4 h-4" /> Save API Test</span>
              )}
            </Button>
          </div>
        </motion.div>
      </div>

      {/* cURL import modal — fills an existing step or appends a new one */}
      <CurlImportModal
        open={curlModalTarget !== null}
        onClose={() => setCurlModalTarget(null)}
        onImport={handleCurlImport}
      />
    </div>
  );
}
